import { Link } from "@tanstack/react-router";

export function PrivacyPage() {
  const lastUpdated = "November 4, 2026";

  return (
    <main className="tj-container max-w-4xl py-12 px-4 sm:px-6" data-testid="privacy-page">
      {/* Breadcrumb Navigation */}
      <div className="mb-6">
        <Link
          to="/"
          className="text-xs font-semibold text-[var(--accent-terracotta)] hover:underline inline-flex items-center gap-1.5"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Home
        </Link>
      </div>

      {/* Header */}
      <header className="border-b border-[var(--border-warm)] pb-8 mb-8">
        <div className="inline-flex items-center gap-2 rounded-full border border-orange-200/80 bg-orange-50/90 px-3.5 py-1 text-xs font-semibold text-[var(--accent-terracotta)] mb-4 shadow-xs">
          <span>Legal &amp; Compliance</span>
        </div>
        <h1 className="serif-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[var(--text-primary)]">
          Privacy Policy
        </h1>
        <p className="mt-3 text-sm text-[var(--text-muted)]">
          Last updated: {lastUpdated} · Governed by the Laws of Hong Kong SAR
        </p>
      </header>

      {/* Policy Content */}
      <div className="space-y-8 text-sm sm:text-base text-[var(--text-secondary)] leading-relaxed">
        {/* Section 1: Introduction & Scope */}
        <section className="space-y-3">
          <h2 className="serif-heading text-xl sm:text-2xl font-bold text-[var(--text-primary)]">
            1. Introduction &amp; Hong Kong PDPO Statement
          </h2>
          <p>
            Tensile Labs Limited (&ldquo;<strong>TaskJinn</strong>&rdquo;, &ldquo;<strong>we</strong>&rdquo;,
            &ldquo;<strong>us</strong>&rdquo;, or &ldquo;<strong>our</strong>&rdquo;) is committed to protecting your
            personal data and respecting your privacy. This Privacy Policy sets out our policies and practices regarding
            the collection, use, retention, disclosure, and protection of personal data in accordance with the{" "}
            <strong>Personal Data (Privacy) Ordinance (Cap. 486 of the Laws of Hong Kong SAR)</strong> (&ldquo;PDPO&rdquo;).
          </p>
          <p>
            By accessing or using our website, services, cloud orchestrators, browser automation agents, and applications
            (collectively, the &ldquo;<strong>Service</strong>&rdquo;), you acknowledge and agree to the practices
            described in this Privacy Policy.
          </p>
        </section>

        {/* Section 2: Zero-Knowledge Architecture & Encryption */}
        <section className="space-y-3 rounded-2xl bg-[var(--bg-card)] border border-[var(--border-warm)] p-6 shadow-xs">
          <div className="flex items-center gap-2.5 text-emerald-800 font-bold text-base sm:text-lg">
            <span className="text-xl">🛡️</span>
            <span>Zero-Knowledge Cryptographic Architecture</span>
          </div>
          <p>
            TaskJinn is built on a zero-knowledge security architecture. User secrets, portal passwords, MFA backup seeds,
            and sensitive credentials are encrypted on your local client device using hardware Passkeys (WebAuthn PRF) and
            client-side key derivation (HKDF-SHA256).
          </p>
          <p className="font-medium text-[var(--text-primary)]">
            Your credentials never leave your browser or client runtime in plaintext. We do not possess the cryptographic
            keys to decrypt your passwords or stored credentials, and our engineers mathematically cannot access them.
          </p>
        </section>

        {/* Section 3: Absolute Commitment: No User Data is Shared */}
        <section className="space-y-3">
          <h2 className="serif-heading text-xl sm:text-2xl font-bold text-[var(--text-primary)]">
            2. Strict Data Sharing Commitment: No User Data is Shared
          </h2>
          <div className="p-4 rounded-xl border border-rose-200 bg-rose-50/60 text-stone-900 space-y-2">
            <p className="font-semibold text-rose-900 flex items-center gap-2">
              <svg className="w-5 h-5 shrink-0 text-rose-700" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              No Sale, Rental, or Sharing of User Data
            </p>
            <p className="text-xs sm:text-sm text-stone-700">
              We do <strong>not</strong> sell, rent, monetize, trade, lease, or share your personal data, customer
              records, or account information with third parties, brokers, or advertising networks under any circumstances.
            </p>
          </div>
          <p>
            We only process your account identifier (e.g. your login email address and cryptographic device public keys)
            strictly to administer your authenticated session, verify platform billing and quotas, and deliver the
            automation services you explicitly invoke.
          </p>
        </section>

        {/* Section 4: Public Website Workflow Templates */}
        <section className="space-y-3">
          <h2 className="serif-heading text-xl sm:text-2xl font-bold text-[var(--text-primary)]">
            3. Public Website Workflow Templates &amp; Automation Blueprints
          </h2>
          <p>
            Our Service enables users to create, configure, and execute automation workflows that interact with publicly
            available web pages, portals, and online services.
          </p>
          <div className="rounded-xl border border-[var(--border-warm)] bg-[var(--bg-subtle)]/70 p-5 space-y-3">
            <h3 className="font-bold text-[var(--text-primary)] text-sm sm:text-base">
              Public Workflow Data Policy:
            </h3>
            <ul className="list-disc pl-5 space-y-2 text-xs sm:text-sm">
              <li>
                <strong>Public Workflow Information:</strong> Information relating to workflow structural steps, DOM
                selectors, navigational paths, execution sequences, and blueprint configurations designed for public
                websites and public portals constitutes non-confidential public workflow data (&ldquo;<strong>Public
                Workflow Data</strong>&rdquo;).
              </li>
              <li>
                <strong>Platform Optimization &amp; Self-Healing Blueprints:</strong> We may view, analyze, compile,
                aggregate, and reuse Public Workflow Data to maintain service stability, optimize adaptive self-healing
                engines, curate template catalogs, and train automated resilience models against website UI redesigns.
              </li>
              <li>
                <strong>Mandatory Redaction of All Private Information:</strong> Prior to any internal access, review,
                or aggregation of Public Workflow Data, <strong>all private, sensitive, and personal information</strong>{" "}
                (including but not limited to personal identifiers, user input values, private payloads, customer details,
                form submissions, account numbers, and credentials) <strong>is strictly removed, filtered, and redacted</strong>.
                Only structural, anonymous public website navigation patterns are analyzed.
              </li>
            </ul>
          </div>
        </section>

        {/* Section 5: Data Retention & Security */}
        <section className="space-y-3">
          <h2 className="serif-heading text-xl sm:text-2xl font-bold text-[var(--text-primary)]">
            4. Data Retention, Security &amp; Cross-Border Transfers
          </h2>
          <p>
            We retain personal data only for as long as necessary to fulfill the operational purposes for which it was
            collected, or to comply with statutory and legal requirements in Hong Kong SAR. When personal data is no longer
            required, it is securely destroyed or irreversibly anonymized.
          </p>
          <p>
            We implement comprehensive technical and organizational safeguards in compliance with Data Protection
            Principle 4 of the PDPO to protect personal data against unauthorized or accidental access, processing, erasure,
            loss, or use.
          </p>
        </section>

        {/* Section 6: Data Subject Rights Under Hong Kong PDPO */}
        <section className="space-y-3">
          <h2 className="serif-heading text-xl sm:text-2xl font-bold text-[var(--text-primary)]">
            5. Your Rights Under Hong Kong Law
          </h2>
          <p>
            Under the Hong Kong Personal Data (Privacy) Ordinance, you have the right to:
          </p>
          <ul className="list-disc pl-5 space-y-1.5 text-xs sm:text-sm">
            <li>Ascertain whether we hold personal data relating to you;</li>
            <li>Request a copy of any personal data we hold about you (Data Access Request);</li>
            <li>Request correction of any personal data that is inaccurate (Data Correction Request); and</li>
            <li>Ascertain our policies and practices in relation to personal data.</li>
          </ul>
          <p className="text-xs sm:text-sm text-[var(--text-muted)]">
            In accordance with the PDPO, we may charge a reasonable administrative fee for processing any data access
            request.
          </p>
        </section>

        {/* Section 7: Inquiries and Contact */}
        <section className="space-y-3 border-t border-[var(--border-warm)] pt-6">
          <h2 className="serif-heading text-xl sm:text-2xl font-bold text-[var(--text-primary)]">
            6. Contacting Us
          </h2>
          <p>
            If you have questions, feedback, or requests regarding this Privacy Policy or your personal data, please contact
            our Data Protection Officer at:
          </p>
          <div className="bg-[var(--bg-card)] border border-[var(--border-warm)] rounded-xl p-4 text-xs sm:text-sm space-y-1">
            <p className="font-bold text-[var(--text-primary)]">Tensile Labs Limited (TaskJinn)</p>
            <p>Attn: Data Protection Officer</p>
            <p>Email: <a href="mailto:privacy@tensilelabs.com" className="text-[var(--accent-terracotta)] hover:underline">privacy@tensilelabs.com</a></p>
            <p>Jurisdiction: Hong Kong Special Administrative Region</p>
          </div>
        </section>
      </div>
    </main>
  );
}

